export interface IAppConfig {
    pbiUrl: string;
    adfUrl: string;
}

export type FabricEnvName =
    | 'Onebox'
    | 'INT'
    | 'TEST'
    | 'DAILY'
    | 'DXT'
    | 'MSIT'
    | 'PROD'
    | 'FF'
    | 'TB'
    | 'TBC'
    | 'PF'
    | 'MC'
    | 'Bleu'
    | 'USNat'
    | 'USSec'
    | 'DEFAULT';

const publicCloudAppConfig: IAppConfig = {
    pbiUrl: 'api.powerbi.com',
    adfUrl: 'https://adf.azure.com'
};

export const fabricToADFEnvDomainMap: Record<FabricEnvName, IAppConfig> = {
    Onebox: {
        pbiUrl: 'api.powerbi.com',
        adfUrl: 'https://adf.azure.com' //dogfood
    },
    INT: {
        pbiUrl: 'api.powerbi.com',
        adfUrl: 'https://adf.azure.com' //dogfood
    },
    TEST: {
        pbiUrl: 'api.powerbi.com',
        adfUrl: 'https://adf.azure.com' //dogfood
    },
    DAILY: {
        pbiUrl: 'dailyapi.powerbi.com',
        adfUrl: 'https://ms-adf.azure.com'
    },
    DXT: {
        pbiUrl: 'powerbistagingapi.analysis.windows.net',
        adfUrl: 'https://ms-adf.azure.com'
    },
    MSIT: {
        pbiUrl: 'api.powerbi.com',
        adfUrl: 'https://ms-adf.azure.com'
    },
    PROD: publicCloudAppConfig,
    FF: {
        pbiUrl: 'api.powerbigov.us',
        adfUrl: 'https://adf.azure.us'
    },
    TB: {
        pbiUrl: 'api.high.powerbigov.us',
        adfUrl: 'https://adf.azure.us'
    },
    // Use production defaults until the TBC and Bleu endpoint pairs are confirmed.
    TBC: publicCloudAppConfig,
    Bleu: publicCloudAppConfig,
    PF: {
        pbiUrl: 'api.mil.powerbigov.us',
        adfUrl: 'https://adf.azure.us'
    },
    MC: {
        pbiUrl: 'api.powerbi.cn',
        adfUrl: 'https://adf.azure.cn'
    },
    USNat: {
        pbiUrl: 'api.powerbi.eaglex.ic.gov',
        adfUrl: 'https://adf.azure.eaglex.ic.gov'
    },
    USSec: {
        pbiUrl: 'api.powerbi.microsoft.scloud',
        adfUrl: 'https://adf.azure.microsoft.scloud'
    },
    DEFAULT: publicCloudAppConfig
};

export function getFabricAppConfig(fabricEnv: FabricEnvName | undefined): IAppConfig {
    return fabricToADFEnvDomainMap[fabricEnv ?? 'PROD'] ?? fabricToADFEnvDomainMap.PROD;
}

/**
 * Read the URL search parameter where the key matches the pattern: feature.dpe_madrid.[flagName]
 * This function will return `undefined` if the flag is not found in the URL.
 * If you want to add default value, please use:
 * ```ts
 * getDPEFeatureFlag('flagName') ?? defaultValue;
 * ```
 * Remember to use `??` operator, not `||` operator. The `??` operator will always honor the value in th URL,
 * which allows you to disable the feature flag by setting the value to `false` in the URL.
 *
 * @param flagName The name of the feature flag
 */
export function getDPEFeatureFlag(flagName: string): boolean | undefined {
    const params = new URLSearchParams(window.location.search);
    const value = params.get(`feature.dpe_madrid.${flagName}`) ?? params.get(`feature.dpe.${flagName}`);
    switch (value) {
        case 'true':
        case '1':
            return true;
        case null:
            return undefined;
        default:
            return false;
    }
}

export class GlobalVariables {
    // #region Fabric-only environment variables
    public static fabricEnv = /[\?&]env\.fabric=(.+?)(?:$|&)/i.exec(window.location.href)?.[1] as FabricEnvName | undefined;
    // FIXME: Decouple this flag from MDF, Aman Jain will follow up on this. https://msdata.visualstudio.com/Azure%20Data%20Factory/_workitems/edit/5288311
    public static isTrident: boolean = /[\?&]feature\.iFrameCustomer=tridentWorkspace/i.test(window.location.href);
    public static isMountedDf: boolean = /[\?&]feature\.iFrameCustomer=mountedDataFactory/i.test(window.location.href);
    // Fabric Mapping Data Flow (MDF). Provided by the host entry page as a global `var __IS_FABRIC_MDF__`.
    // shell-models-lib has no compile-time define for this global, so read it from window (defaults to false).
    public static isTridentMdf: boolean = (window as unknown as { __IS_FABRIC_MDF__?: boolean }).__IS_FABRIC_MDF__ === true;
    public static enableIFrameMode: boolean = /[\?&]feature\.enableIFrameMode=true/i.test(window.location.href);
    // #endregion

    public static isDogfood: boolean =
        /[\?&]environment=dogfood/i.test(window.location.href) ||
        ['Onebox', 'INT', 'TEST'].includes(this.fabricEnv);

    public static isMicrosoftInternal: boolean = this.fabricEnv === 'MSIT';

    // Do not remove, customer workaround, temporary alternative for customers who are unwilling to change timeout
    public static activityRunTimeLimit = !/[\?&]feature\.activityRunTimeLimit=false/i.test(window.location.href); // Do not clean up

    public static enablePreventDataExfiltration = /[\?&]feature\.preventDataExfiltration=true/i.test(
        window.location.href
    );

    // Owner: soro, 2022/03/09
    public static useLakeDB2021Api = /[\?&]feature\.lakeDB2021Api=true/i.test(window.location.href);

    // jenqiao 5/22
    public static get strictParamValidation(): boolean {
        return (
            /[\?&]feature\.strictParamValidation=true/i.test(window.location.href) ||
            (GlobalVariables.isMicrosoftInternal &&
                !/[\?&]feature\.strictParamValidation=false/i.test(window.location.href))
        );
    }
    public static serviceNowParamedLs: boolean = /[\?&]feature\.enableParameterizationInServiceNowLs=true/i.test(window.location.href); // service now LS parameterization

    public static enableSamiSupport: boolean = /[\?&]feature\.enableSami=true/i.test(window.location.href);

    //Onwer: kaixuanxu, 2024/08/22 -SFTP winSCP support
    public static enableSFTPV2: boolean = /[\?&]feature\.enableSFTPV2=true/i.test(window.location.href);

    // since this is only used for LS, keeping this untouched.
    public static enableUserAuthCred: boolean = /[\?&]feature\.enableUserAuthCred=true/i.test(window.location.href);
    public static enableConnectorUserAuthCred: boolean =
        this.enableUserAuthCred && /[\?&]feature\.enableConnectorUserAuthCred=true/i.test(window.location.href);

    public static isCmdApiApp = false;
    public static maxActivityLimit: number = 120;
    public static enableLargeActivityLimit: boolean = /[\?&]feature\.largeActivityLimit=true/i.test(
        window.location.href
    );

    //Owner: yuanjieluo, 2024/04/14
    public static enableKQLDMTSCredential: boolean = getDPEFeatureFlag('enableKQLDMTSCredential') && GlobalVariables.isTrident;
    //Owner: yuanjieluo, 2024/04/18
    public static enableSQLAnalyticsEndpoint: boolean = /[\?&]feature\.sqlAnalyticsEndpoint=true/i.test(window.location.href);

    public static disableNestedDependencyCalculation: boolean = /[\?&]feature\.disableNestedDependencyCalculation=true/i.test(window.location.href);  // ADF only, migration of v1 to v2
    public static enableServiceNowV2Partition: boolean = /[\?&]feature\.enableServiceNowV2Partition=true/i.test(window.location.href) && !GlobalVariables.isTrident;
}
